/**
 * F12 Sprint E — Copilot Store (item #327)
 *
 * Manages Studio Copilot palette open/close state, pin mode,
 * and chat history within the current browser session.
 */

import { create } from 'zustand';
import type { CopilotToolCall } from '../services/copilot.service.js';

export interface CopilotMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: CopilotToolCall[];
  /** Present when a write tool is pending HITL approval */
  pendingApproval?: {
    correlationId: string;
    toolName: string;
  };
  timestamp: Date;
}

interface CopilotState {
  /** Whether the Cmd+I Copilot palette modal is visible */
  isOpen: boolean;
  /** Whether copilot is pinned as sidebar panel */
  isPinned: boolean;
  /** Current conversation session ID (maps to ai_conversations.id) */
  sessionId: string | undefined;
  /** In-memory chat history for the current session */
  messages: CopilotMessage[];
  /** Whether a request is in-flight */
  isLoading: boolean;

  // Actions
  open: () => void;
  close: () => void;
  toggle: () => void;
  pin: () => void;
  unpin: () => void;
  togglePin: () => void;
  setSessionId: (id: string | undefined) => void;
  addMessage: (msg: Omit<CopilotMessage, 'id' | 'timestamp'>) => void;
  updateLastMessage: (patch: Partial<Omit<CopilotMessage, 'id' | 'timestamp'>>) => void;
  clearHistory: () => void;
  setLoading: (val: boolean) => void;
}

function uuid(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export const useCopilotStore = create<CopilotState>((set) => ({
  isOpen: false,
  isPinned: false,
  sessionId: undefined,
  messages: [],
  isLoading: false,

  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  pin: () => set({ isPinned: true, isOpen: false }),
  unpin: () => set({ isPinned: false }),
  togglePin: () => set((s) => ({
    isPinned: !s.isPinned,
    isOpen: s.isPinned ? false : s.isOpen,
  })),
  setSessionId: (id) => set({ sessionId: id }),
  addMessage: (msg) =>
    set((s) => ({
      messages: [...s.messages, { ...msg, id: uuid(), timestamp: new Date() }],
    })),
  updateLastMessage: (patch) =>
    set((s) => {
      if (s.messages.length === 0) return s;
      const updated = [...s.messages];
      updated[updated.length - 1] = { ...updated[updated.length - 1]!, ...patch };
      return { messages: updated };
    }),
  clearHistory: () => set({ messages: [], sessionId: undefined }),
  setLoading: (val) => set({ isLoading: val }),
}));
